import React, { useState, useMemo, useEffect } from 'react';
import { Typography, RichTextViewer, TextButton } from 'seagull';
import RichTextViewerModal from "../../../components/RichTextViewerModal";
import { useOverlayContext, useIoContext, useStore, useStoreActions } from "../../../contexts";
import StatementOfWork from "./components/StatementOfWork";
import { AgreementViewCard, SpecificTerm } from "./components/CommonComponents";
import Header from "./components/Header";
import styles from "./AgreementView.module.scss";
import { legalTermsModel, relationshipModel } from "../../../models";
import SignedHeader from "./components/SignedHeader";
import ChangesSummary from "./components/ChangesSummary";
import AgreementAmendmentsApprovalMethod from "./components/AgreementAmendmentsApprovalMethod";
import { getCoverCCFeesChangeBullet, getCoverCCFeesText, getNetTermsChangeBullet } from "../../../business-logic/amendmentsLogic";
import AmendmentChangesInAgreementView from "./components/AmendmentChangesInAgreementView";
import { selectors } from "../../../store/state-selectors";
import AgreementInvoicesApprovalMethod from "./components/AgreementInvoicesApprovalMethod";
import { AgreementDataModel } from "../../../models/AgreementDataModel";
const autoprefix = 'agreement-view-';
const pdfViewUrlLoader = 'pdfViewUrl';
export default function AgreementView(props) {
  const {
    relationship,
    isVendor,
    refresh
  } = props;
  const overlayContext = useOverlayContext();
  const {
    openNewTab
  } = useIoContext();
  const {
    extraClause
  } = relationship;
  const {
    buttonPrimary
  } = useStore(selectors.user.branding);
  const qboClasses = useStore(state => state.qboClasses.data);
  const {
    api,
    fetchQboClasses
  } = useStoreActions();
  const [showViewerModal, setShowViewerModal] = useState(false);
  const [legalTermsUrl, setLegalTermsUrl] = useState();
  const updatedNetTerms = relationshipModel.updatedNetTerms(relationship);
  const netTermsChangeBullet = updatedNetTerms ? getNetTermsChangeBullet(relationship, updatedNetTerms.value()) : null;
  const updatedIsVendorCoveringCCFee = relationshipModel.updatedIsVendorCoveringCCFee(relationship);
  const agreementData = AgreementDataModel.fromRelationship(relationship);
  useEffect(() => {
    fetchQboClasses();
  }, []);
  const coverCCFeesChangeBullet = updatedIsVendorCoveringCCFee ? getCoverCCFeesChangeBullet(relationship, updatedIsVendorCoveringCCFee.value()) : null;
  const richText = useMemo(() => {
    return legalTermsModel.richTextContent(relationship.legalTerms);
  }, [relationship]);
  const effectiveDateTerm = relationship.effectiveDate && /*#__PURE__*/React.createElement(SpecificTerm, {
    field: "Effective date of agreement",
    value: relationshipModel.formatEffectiveDate(relationship),
    testid: autoprefix + 'effective-date'
  });
  async function onViewLegalTerms() {
    if (legalTermsModel.isPDF(relationship.legalTerms)) {
      const url = await getLegalTermsUrl(relationship.legalTerms);
      openNewTab(url);
    } else {
      setShowViewerModal(true);
    }
  }
  async function getLegalTermsUrl(legalTerms) {
    if (legalTermsUrl) {
      return legalTermsUrl;
    }
    overlayContext.startLoader(pdfViewUrlLoader);
    const pdfViewUrl = await api.business.getLegalTermsDownloadURL(legalTerms.pdfUniqueName, relationship.vendor.id);
    if (!pdfViewUrl) {
      throw new Error('legalTermsUrlError');
    }
    setLegalTermsUrl(pdfViewUrl);
    overlayContext.stopLoader(pdfViewUrlLoader);
    return pdfViewUrl;
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Header, {
    relationship: relationship,
    onUpdate: refresh
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.main,
    "data-testid": "agreement-view"
  }, /*#__PURE__*/React.createElement(ChangesSummary, {
    isVendor: isVendor,
    relationship: relationship,
    testid: autoprefix + 'changes',
    refresh: refresh
  }), /*#__PURE__*/React.createElement(SignedHeader, {
    relationship: relationship,
    brandColor: buttonPrimary,
    testPrefix: autoprefix
  }), /*#__PURE__*/React.createElement(AgreementViewCard, {
    headTitle: "Services Provided"
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.services
  }, /*#__PURE__*/React.createElement(StatementOfWork, {
    services: relationship.services,
    isVendor: isVendor,
    qboClasses: qboClasses,
    serviceDetailsProps: {
      agreementData,
      isVendor
    }
  }))), /*#__PURE__*/React.createElement(AgreementViewCard, {
    headTitle: "Agreement terms"
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.specificTerms
  }, effectiveDateTerm, /*#__PURE__*/React.createElement(SpecificTerm, {
    field: "Payment terms",
    value: relationshipModel.paymentTermsText(relationship),
    testid: autoprefix + 'net-terms',
    changes: netTermsChangeBullet && /*#__PURE__*/React.createElement(AmendmentChangesInAgreementView, {
      disableBorderRadius: true,
      items: [netTermsChangeBullet],
      autoApprovalDate: relationshipModel.updatedNetTermsAutoApprovalDate(relationship),
      testid: autoprefix + 'net-terms-changes'
    })
  }), /*#__PURE__*/React.createElement(SpecificTerm, {
    testid: autoprefix + 'cc-fees',
    field: "Credit card fees",
    value: getCoverCCFeesText(relationshipModel.actualIsVendorCoveringCCFee(relationship), relationshipModel.vendorName(relationship), relationshipModel.customerName(relationship)),
    changes: coverCCFeesChangeBullet && /*#__PURE__*/React.createElement(AmendmentChangesInAgreementView, {
      disableBorderRadius: true,
      items: [coverCCFeesChangeBullet],
      autoApprovalDate: relationshipModel.updatedIsVendorCoveringCCFeeAutoApprovalDate(relationship),
      testid: autoprefix + 'cc-fees-changes'
    })
  }), /*#__PURE__*/React.createElement(AgreementAmendmentsApprovalMethod, {
    relationship: relationship,
    testid: autoprefix
  }), /*#__PURE__*/React.createElement(AgreementInvoicesApprovalMethod, {
    relationship: relationship,
    testid: autoprefix
  }), extraClause && /*#__PURE__*/React.createElement(SpecificTerm, {
    field: "Notes",
    value: /*#__PURE__*/React.createElement(RichTextViewer, {
      "data-testid": 'extra-clause-rich-text-viewer',
      value: extraClause,
      skin: "secondary"
    })
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.tnc
  }, /*#__PURE__*/React.createElement(Typography, {
    weight: "bold"
  }, "Additional terms of service:"), "\xA0", /*#__PURE__*/React.createElement(TextButton, {
    "data-testid": autoprefix + "btn-view",
    onClick: onViewLegalTerms
  }, "Read more")), showViewerModal && /*#__PURE__*/React.createElement(RichTextViewerModal, {
    show: true,
    title: relationship.legalTerms.name,
    text: richText,
    onCancel: () => setShowViewerModal(false)
  })))));
}